import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';

export default function RefundPolicy() {
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-12">
        <h1 className="font-heading text-3xl sm:text-4xl font-medium text-[#1A1A1A] mb-2" data-testid="refunds-title">Refund &amp; Cancellation Policy</h1>
        <p className="text-sm text-stone-400 mb-8">Last updated: June 2026</p>

        <div className="prose prose-stone max-w-none space-y-6 text-sm leading-relaxed text-stone-600">
          <section>
            <p>
              Zuriya Ltd (&quot;Zuriya&quot;, &quot;we&quot;) operates a two-sided marketplace. Each Provider
              on Zuriya sets their own cancellation policy from one of three tiers, Flexible, Moderate, or
              Strict, and that tier applies to every booking made with them. The tier in force is displayed
              on the Provider&apos;s profile and in the booking summary before you pay. By confirming a
              booking you agree to the policy that Provider has chosen, the platform charges described
              below, and the refund and fee-capture mechanics set out in this document.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">1. Provider Cancellation Tiers</h2>
            <p className="mb-3">
              Each refund entitlement below is calculated against the total booking value (service price plus any add-ons).
              Deposits paid at the time of booking are treated as part of the total and refunded in line with the tier.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-[#E6E4DD] rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-[#F9F6F0]">
                    <th className="text-left py-2 px-3 font-medium text-[#1A1A1A] w-[20%]">Tier</th>
                    <th className="text-left py-2 px-3 font-medium text-[#1A1A1A]">Cancellation window</th>
                    <th className="text-left py-2 px-3 font-medium text-[#1A1A1A]">Refund / Fee</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-[#E6E4DD] align-top">
                    <td className="py-3 px-3 font-medium text-emerald-700">Flexible</td>
                    <td className="py-3 px-3">Anytime before the appointment</td>
                    <td className="py-3 px-3 font-medium text-emerald-600">Full refund. No-shows are not charged.</td>
                  </tr>
                  <tr className="border-t border-[#E6E4DD] align-top">
                    <td className="py-3 px-3 font-medium text-amber-700" rowSpan="3">Moderate</td>
                    <td className="py-3 px-3">48 hours or more before the appointment</td>
                    <td className="py-3 px-3 font-medium text-emerald-600">Full refund</td>
                  </tr>
                  <tr className="border-t border-[#E6E4DD] align-top">
                    <td className="py-3 px-3">Less than 48 hours before the appointment</td>
                    <td className="py-3 px-3 font-medium text-amber-600">50% of the booking total retained by the Provider as a late-cancellation fee</td>
                  </tr>
                  <tr className="border-t border-[#E6E4DD] align-top">
                    <td className="py-3 px-3">No-show</td>
                    <td className="py-3 px-3 font-medium text-red-600">
                      100% of the booking total may be charged, including via a card held on file (see Section 3).
                      A no-show leaves no opportunity to rebook the slot.
                    </td>
                  </tr>
                  <tr className="border-t border-[#E6E4DD] align-top">
                    <td className="py-3 px-3 font-medium text-red-700">Strict</td>
                    <td className="py-3 px-3">Deposits and any payments are <strong>non-refundable</strong> from the moment the booking is confirmed</td>
                    <td className="py-3 px-3 font-medium text-red-600">
                      Late-cancellation (&lt;48 hours) or no-show: up to 100% of the booking total may be charged,
                      including via a card held on file (see Section 3).
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="mt-3 text-xs text-stone-500">
              &quot;Cancellation window&quot; is measured from the scheduled start of the appointment (Europe/London local time)
              back to the moment you submit the cancellation through the platform, not to the moment the Provider acknowledges it.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">2. How to Cancel or Reschedule</h2>
            <p>
              Self-service cancellation and rescheduling is available directly from your account:
            </p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li><strong>Registered clients:</strong> sign in and open the booking from your <Link to="/dashboard" className="text-[#D4AF37] underline">dashboard</Link>. The refund preview is displayed before you confirm.</li>
              <li><strong>Guest bookings:</strong> use the secure &quot;Manage booking&quot; link in your confirmation email; no sign-in is required.</li>
              <li>If a Provider accepts a reschedule request, any fees that would have applied to a cancellation at that point are waived.</li>
            </ul>
            <p className="mt-2">
              If you cannot access these tools, email <a href="mailto:support@zuriya.co.uk" className="text-[#D4AF37] underline">support@zuriya.co.uk</a> with your booking reference and we will action the change on your behalf within one working day.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">3. Card on File, Off-Session Charges &amp; Stripe</h2>
            <p className="mb-2">
              Bookings made under a <strong>Moderate</strong> or <strong>Strict</strong> tier where no deposit (or only a partial
              deposit) is taken require you to securely save a payment method so the Provider&apos;s cancellation and no-show fees
              can be enforced. This is disclosed on-screen before you confirm the booking.
            </p>
            <p className="mb-2">
              <strong>Off-session charging.</strong> By confirming a booking that requires a card on file you authorise
              Zuriya and Stripe Payments UK, Ltd. (&quot;Stripe&quot;) to charge that card, on an off-session basis and without
              further prompting, for late-cancellation or no-show fees that fall due under the Provider&apos;s chosen tier
              (Section 1). The maximum possible charge for the booking is shown to you before checkout and will never exceed 100%
              of the booking total. Additional authentication (3-D Secure) may still be required by your issuing bank.
            </p>
            <p className="mb-2">
              <strong>Provider grace period.</strong> Once a late-cancel or no-show is recorded, the fee is scheduled
              for capture but held for a <strong>24-hour provider review window</strong>. During this window the Provider
              can waive the fee in full. Zuriya does not override a Provider&apos;s decision to waive.
            </p>
            <p className="mb-2">
              <strong>Payment processor.</strong> All payments (deposits, full payments, and off-session fee captures)
              are processed by <a href="https://stripe.com/gb/privacy" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] underline">Stripe</a>.
              Zuriya does not store card numbers or CVVs; we hold only a reference token returned by Stripe that is scoped
              to your account. Provider payouts are routed via Stripe Connect (Express accounts).
            </p>
            <p>
              <strong>Managing saved cards.</strong> You can view and remove cards on file at any time from
              <strong> Dashboard → Payment methods</strong>. A card cannot be removed while it is linked to an upcoming
              booking that requires it; cancel or reschedule that booking first.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">4. Deposits</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>Providers may require a deposit between 20% and 100% of the service price at the time of booking.</li>
              <li>Clients never pay a booking fee. A deposit is part of the service price, not an extra charge, and the balance due at the appointment is shown before you pay.</li>
              <li>Deposits form part of the total booking value and are refunded (or retained) in line with the tier in Section 1.</li>
              <li>If a Provider cancels, any deposit is refunded in full regardless of tier (see Section 5).</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">5. Cancellation by the Provider</h2>
            <p>
              If a Provider cancels a confirmed booking, the client receives a <strong>full refund</strong> of anything paid
              (deposit or full payment) regardless of the tier, and no card-on-file fee is captured. Repeated cancellations
              by a Provider may result in account review, removal from featured placements, or suspension under our
              <Link to="/terms" className="text-[#D4AF37] underline"> Terms of Service</Link>.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">6. Auto-Completion &amp; 48-Hour Dispute Window</h2>
            <p>
              Confirmed bookings are automatically marked <strong>Completed</strong> two hours after the scheduled end of the
              appointment if neither party has updated the status. This keeps payouts on schedule and reduces admin load.
            </p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Both parties receive an email and in-app notification confirming auto-completion.</li>
              <li>Clients then have a <strong>48-hour window</strong> from auto-completion to raise a dispute via the &quot;Report an Issue&quot; button on the completed booking.</li>
              <li>Raising a dispute places the Provider&apos;s earnings for that booking on hold while our team reviews the case. A fair outcome may include a partial or full refund, or a replacement service.</li>
              <li>After the 48-hour window closes, the booking is considered settled and funds clear to the Provider at the next payout cycle.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">7. Service Disputes &amp; Mediation</h2>
            <p>
              Beyond the 48-hour auto-completion window, if you believe a service you received was materially different
              from what was advertised, or if you are a Provider and the client&apos;s conduct breached our Terms, contact
              <a href="mailto:support@zuriya.co.uk" className="text-[#D4AF37] underline"> support@zuriya.co.uk</a> within 14 days.
              We will mediate impartially and may, at our discretion, authorise a refund, waive a fee, or arrange a goodwill credit.
              Nothing in this policy removes your statutory rights under UK consumer law.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">8. How Refunds Are Processed</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>Refunds are issued to the original payment method via Stripe.</li>
              <li>
                <strong>Platform fee.</strong> Zuriya&apos;s platform fee is taken from the Provider&apos;s share, never from the
                Client. It never reduces a refund: when a payment is refunded, in full or in part, Zuriya also returns its
                platform fee on the refunded amount.
              </li>
              <li>Allow <strong>5–10 UK business days</strong> for the refund to appear on your statement after we process it. Some card issuers are faster; very few take longer.</li>
              <li>
                Stripe&apos;s own card-processing fees on the original payment are non-refundable to Zuriya, where a
                Provider would otherwise bear the full fee, we absorb this on their behalf as a goodwill commitment.
              </li>
              <li>Any referral or platform credit applied at checkout is returned to your Zuriya balance, not to your card.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">9. Chargebacks</h2>
            <p>
              Please contact us before raising a chargeback with your bank, in almost all cases we can resolve the issue
              directly. Chargebacks filed for bookings that were legitimately delivered or that sit under a Strict policy
              will be defended with Stripe using the booking record, messages, and any photographic evidence supplied by
              the Provider. Accounts that abuse the chargeback process may be suspended.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">10. Contact</h2>
            <p>
              For refund and cancellation queries, email
              <a href="mailto:support@zuriya.co.uk" className="text-[#D4AF37] underline"> support@zuriya.co.uk</a> with your
              booking reference. For privacy-related questions, see our
              <Link to="/privacy" className="text-[#D4AF37] underline"> Privacy Policy</Link> or write to
              <a href="mailto:privacy@zuriya.co.uk" className="text-[#D4AF37] underline"> privacy@zuriya.co.uk</a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
